import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PRIORITIES, STATUSES, PRIORITY_COLOR, STATUS_COLOR, BILLING_COLOR, fmtDuration, fmtDate } from "@/lib/supportApi";
import { ArrowLeft, ArrowCounterClockwise, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TicketDetail() {
  const { tid } = useParams();
  const { currentOrgId } = useAuth();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    if (currentOrgId) api.get(`/support/tickets/${tid}?org_id=${currentOrgId}`).then((r) => setT(r.data)).catch(() => {});
  }, [tid, currentOrgId]);
  useEffect(load, [load]);
  if (!t) return <div className="text-zinc-500 text-sm">Loading…</div>;

  const patch = async (body) => {
    try { await api.patch(`/support/tickets/${tid}`, { org_id: currentOrgId, ...body }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const reopen = async () => { try { await api.post(`/support/tickets/${tid}/reopen`, { org_id: currentOrgId }); toast.success("Reopened"); load(); } catch { toast.error("Failed"); } };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/support/tickets/${tid}/notes`, { org_id: currentOrgId, body: note, visibility: "internal" }); setNote(""); load(); }
    catch { toast.error("Could not add note"); }
  };

  return (
    <div className="space-y-5" data-testid="ticket-detail">
      <button onClick={() => navigate("/helpdesk/tickets")} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white"><ArrowLeft size={15} /> Tickets</button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight"><span className="font-mono text-zinc-500 text-lg mr-2">#{t.ticket_number}</span>{t.subject}</h1>
          <div className="flex items-center gap-2 mt-2">
            <span data-testid="ticket-customer-name" className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 px-2.5 py-1 text-sm font-medium text-blue-200">
              <Buildings size={14} weight="fill" /> {t.customer_name || "No customer"}
            </span>
          </div>
          <div className="text-sm text-zinc-500 mt-1.5">Opened {fmtDate(t.opened_at)} · reopened {t.reopen_count}×</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={t.priority} onValueChange={(v) => patch({ priority: v })}>
            <SelectTrigger data-testid="ticket-priority-select" className="w-32 bg-black border-zinc-800 h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={t.status} onValueChange={(v) => patch({ status: v })}>
            <SelectTrigger data-testid="ticket-status-select" className="w-44 bg-black border-zinc-800 h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {(t.status === "Closed" || t.status === "Resolved") && (
            <Button data-testid="ticket-reopen-button" variant="outline" onClick={reopen} className="h-9 border-zinc-700"><ArrowCounterClockwise size={14} className="mr-1.5" /> Reopen</Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4">
            <h3 className="text-xs uppercase tracking-wider text-zinc-400 mb-2">Description</h3>
            <div className="text-sm text-zinc-300 whitespace-pre-wrap">{t.description || "—"}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4">
            <h3 className="text-xs uppercase tracking-wider text-zinc-400 mb-3">Work performed / resolution</h3>
            <Textarea data-testid="ticket-work-performed" defaultValue={t.work_performed || ""} onBlur={(e) => e.target.value !== (t.work_performed || "") && patch({ work_performed: e.target.value })} rows={3} className="bg-black border-zinc-800" placeholder="What was done…" />
          </div>
          <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4">
            <h3 className="text-xs uppercase tracking-wider text-zinc-400 mb-3">Internal notes</h3>
            <div className="space-y-2 mb-3">
              {t.notes.map((n) => (
                <div key={n.note_id} className="text-sm rounded-lg border border-white/10 bg-black/40 px-3 py-2">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider mb-1">
                    <span className={n.visibility === "customer" ? "text-sky-400" : "text-zinc-500"}>{n.visibility}</span>
                    <span className="text-zinc-600">{fmtDate(n.created_at)}</span>
                  </div>
                  <div className="text-zinc-300 whitespace-pre-wrap">{n.body}</div>
                </div>
              ))}
              {t.notes.length === 0 && <div className="text-sm text-zinc-500">No notes yet</div>}
            </div>
            <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="bg-black border-zinc-800" placeholder="Add an internal note…" />
            <div className="flex items-center justify-end mt-2">
              <Button data-testid="add-note-button" onClick={addNote} size="sm" className="bg-blue-600 hover:bg-blue-500">Add note</Button>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4">
            <h3 className="text-xs uppercase tracking-wider text-zinc-400 mb-2">Time entries</h3>
            {t.time_entries.length === 0 ? <div className="text-sm text-zinc-500">None</div> :
              t.time_entries.map((e) => (
                <div key={e.entry_id} className="flex items-center justify-between py-1.5 text-sm border-b border-white/5 last:border-0">
                  <span>{fmtDuration(e.actual_seconds)} <span className="text-xs text-zinc-500">· {e.source}</span></span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${BILLING_COLOR[e.billing_status]}`}>{e.billing_status}</span>
                </div>
              ))}
          </div>
          <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4">
            <h3 className="text-xs uppercase tracking-wider text-zinc-400 mb-2">Activity</h3>
            <div className="space-y-2">
              {t.activity.map((a) => (
                <div key={a.activity_id} className="text-xs text-zinc-400">
                  <span className="text-zinc-300">{a.type.replace(/_/g, " ")}</span>
                  {a.from_value != null && <span> · {String(a.from_value)} → {String(a.to_value)}</span>}
                  <div className="text-zinc-600">{fmtDate(a.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
